import { Dispatch, FC, memo, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Avatar, Button, Col, Input, Row } from 'antd';
import {
    TbBuildingBank,
    TbCircleFilled,
    TbColumnInsertLeft,
    TbFileCheck,
    TbLayoutKanban,
    TbLayoutList,
    TbLibrary,
    TbRoute,
    TbScale,
    TbSearch,
    TbTrash,
    TbUsersGroup,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { CompactCollapsible } from '@/shared';
import SelectableOptionCard from '@/shared/components/cards/SelectableOptionCard';
import {
    DragDropContext,
    Draggable,
    DraggableStyle,
    DragHandle,
    Droppable,
    DropResult,
} from '@/shared/components/drag-and-drop';
import DropdownButtonSelect from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { PlatformForm as Form, PlatformColorPicker } from '@/shared/components/forms';
import { PipelineColumn, PipelineView } from '@/types';

import { PipelineTemplateDefinition, PipelineTemplateId } from '../../templates';

interface NewPipelineProps {
    columns: Partial<PipelineColumn>[];
    input: string;
    layout: PipelineView;
    onTemplateChange: (templateId: PipelineTemplateId) => void;
    selectedTemplateId: PipelineTemplateId;
    setColumns: Dispatch<SetStateAction<Partial<PipelineColumn>[]>>;
    setInput: Dispatch<SetStateAction<string>>;
    setLayout: Dispatch<SetStateAction<PipelineView>>;
    templates: PipelineTemplateDefinition[];
}

const templateIcons: Record<PipelineTemplateId, JSX.Element> = {
    default: <TbRoute />,
    research: <TbSearch />,
    'mergers-acquisitions': <TbBuildingBank />,
    'due-diligence': <TbFileCheck />,
    compliance: <TbScale />,
    fundraising: <TbUsersGroup />,
};

const NewPipeline: FC<NewPipelineProps> = ({
    columns,
    input,
    layout,
    onTemplateChange,
    selectedTemplateId,
    setColumns,
    setInput,
    setLayout,
    templates,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [templatesOpen, setTemplatesOpen] = useState(true);
    const addColumn = useCallback(() => {
        if (columns.length >= 8) {
            return;
        }

        const nextIndex = Math.max(-1, ...columns.map((column) => Number(column.index) || 0)) + 1;
        setColumns([...columns, { color: token.colorTextTertiary, index: nextIndex, name: 'New Stage' }]);
    }, [columns, setColumns, token.colorTextTertiary]);
    const removeColumn = useCallback(
        (index: number) => {
            if (columns.length > 4) {
                setColumns(columns.filter((_, columnIndex) => columnIndex !== index));
            }
        },
        [columns, setColumns],
    );
    const handleInputChange = useCallback(
        (index: number, field: 'color' | 'name', value: string) => {
            setColumns((current) =>
                current.map((column, columnIndex) => (columnIndex === index ? { ...column, [field]: value } : column)),
            );
        },
        [setColumns],
    );
    const getStageRowStyle = useCallback(
        (isDragging: boolean, draggableStyle?: DraggableStyle) => ({
            backgroundColor: isDragging ? token.colorBgElevated : undefined,
            border: `1px solid ${isDragging ? token.colorBorder : 'transparent'}`,
            borderRadius: token.borderRadius,
            boxShadow: isDragging ? token.boxShadowSecondary : undefined,
            boxSizing: 'border-box' as const,
            margin: isDragging ? 0 : '0.25rem 0',
            padding: '0.25rem',
            transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
            ...draggableStyle,
        }),
        [token.borderRadius, token.boxShadowSecondary, token.colorBgElevated, token.colorBorder],
    );
    const onDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) {
                return;
            }

            const reorderedColumns = [...columns];
            const movedItems = reorderedColumns.splice(result.source.index, 1);
            const movedItem = movedItems[0];

            if (!movedItem) {
                return;
            }
            reorderedColumns.splice(result.destination.index, 0, movedItem);
            setColumns(reorderedColumns.map((column, index) => ({ ...column, index })));
        },
        [columns, setColumns],
    );
    const stagesEditor = useMemo(
        () => (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId='columns'>
                    {(droppableProvided) => (
                        <>
                            <div
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                                style={{
                                    backgroundColor: token.colorBgBase,
                                    border: `1px dashed ${token.colorBorder}`,
                                    borderRadius: token.borderRadius,
                                    padding: '0.25rem',
                                    transition: 'background-color 160ms ease, border-color 160ms ease',
                                }}
                            >
                                <Form layout='vertical' style={{ margin: 0 }}>
                                    {columns.map((column, index) => (
                                        <Draggable
                                            draggableId={String(column.index)}
                                            index={index}
                                            key={String(column.index)}
                                        >
                                            {(draggableProvided, draggableSnapshot) => (
                                                <Row
                                                    align='middle'
                                                    gutter={[8, 0]}
                                                    ref={draggableProvided.innerRef}
                                                    wrap={false}
                                                    {...draggableProvided.draggableProps}
                                                    style={getStageRowStyle(
                                                        draggableSnapshot.isDragging,
                                                        draggableProvided.draggableProps.style,
                                                    )}
                                                >
                                                    <Col flex='none'>
                                                        <DragHandle
                                                            dragHandleProps={draggableProvided.dragHandleProps}
                                                            isDragging={draggableSnapshot.isDragging}
                                                            label={`Reorder ${column.name ?? 'stage'}`}
                                                        />
                                                    </Col>
                                                    <Col flex='none'>
                                                        <PlatformColorPicker
                                                            onChange={(color) =>
                                                                handleInputChange(index, 'color', color.toHexString())
                                                            }
                                                            value={column.color}
                                                        >
                                                            <Button
                                                                aria-label={`Color for ${column.name ?? 'stage'}`}
                                                                icon={
                                                                    <TbCircleFilled
                                                                        size='1rem'
                                                                        style={{ color: column.color }}
                                                                    />
                                                                }
                                                                size='small'
                                                            />
                                                        </PlatformColorPicker>
                                                    </Col>
                                                    <Col flex='auto'>
                                                        <Input
                                                            onChange={(event) =>
                                                                handleInputChange(index, 'name', event.target.value)
                                                            }
                                                            placeholder='Stage Name...'
                                                            value={column.name}
                                                        />
                                                    </Col>
                                                    <Col flex='none'>
                                                        <Button
                                                            aria-label={`Delete ${column.name ?? 'stage'}`}
                                                            danger
                                                            disabled={columns.length <= 4}
                                                            icon={<TbTrash />}
                                                            onClick={() => removeColumn(index)}
                                                            size='small'
                                                            type='text'
                                                        />
                                                    </Col>
                                                </Row>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                </Form>
                            </div>
                            <Button
                                block
                                disabled={columns.length >= 8}
                                icon={<TbColumnInsertLeft size='1rem' />}
                                onClick={addColumn}
                                style={{ marginTop: '0.75rem' }}
                                type='dashed'
                            >
                                Add Stage
                            </Button>
                        </>
                    )}
                </Droppable>
            </DragDropContext>
        ),
        [
            addColumn,
            columns,
            getStageRowStyle,
            handleInputChange,
            onDragEnd,
            removeColumn,
            token.borderRadius,
            token.colorBgBase,
            token.colorBorder,
        ],
    );

    return (
        <>
            <Form layout='vertical' requiredMark={false}>
                <Row align='bottom' gutter={[12, 0]}>
                    <Col flex='none'>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Avatar
                                icon={<TbRoute style={{ color: token.colorTextTertiary }} />}
                                shape='square'
                                size={32}
                                style={{ border: `1px solid ${token.colorBorder}` }}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex='auto'>
                        <Form.Item label='Name' required style={{ marginBottom: 0 }}>
                            <Input
                                onChange={(event) => setInput(event.target.value)}
                                placeholder='SEA Next 3 Quarters...'
                                value={input}
                            />
                        </Form.Item>
                    </Col>
                    <Col flex='14rem'>
                        <Form.Item label='Pipeline Layout' style={{ marginBottom: 0 }}>
                            <DropdownButtonSelect<PipelineView>
                                options={[
                                    {
                                        description: 'Organize Pipeline cards across stage-based columns.',
                                        icon: <TbLayoutKanban size='1rem' />,
                                        label: 'Kanban',
                                        value: PipelineView.KANBAN,
                                    },
                                    {
                                        description: 'Review Pipeline cards in a compact, grouped list.',
                                        icon: <TbLayoutList size='1rem' />,
                                        label: 'List',
                                        value: PipelineView.LIST,
                                    },
                                ]}
                                value={layout}
                                onChange={(nextLayout) => {
                                    if (nextLayout) {
                                        setLayout(nextLayout);
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <CompactCollapsible
                bordered
                icon={
                    <Avatar
                        icon={<TbLibrary />}
                        shape='square'
                        size='small'
                        style={{
                            backgroundColor: token.colorBgContainer,
                            border: `1px solid ${token.colorBorder}`,
                            color: token.colorTextTertiary,
                        }}
                    />
                }
                label='Pipeline Templates'
                open={templatesOpen}
                sectionStyle={{ marginTop: '0.75rem' }}
                onOpenChange={setTemplatesOpen}
            >
                <Row gutter={[16, 16]} role='radiogroup'>
                    {templates.map((template, templateIndex) => (
                        <Col key={template.id} span={12} style={{ display: 'flex' }}>
                            <SelectableOptionCard
                                borderStyle={templateIndex === 0 ? 'solid' : 'dashed'}
                                description={template.description}
                                icon={
                                    <Avatar
                                        icon={templateIcons[template.id]}
                                        shape='square'
                                        size='small'
                                        style={{
                                            backgroundColor:
                                                templateIndex === 0 ? token.colorBgContainer : token.colorPrimaryBg,
                                            border: `1px solid ${
                                                templateIndex === 0 ? token.colorBorder : token.colorPrimaryBorder
                                            }`,
                                            color: templateIndex === 0 ? token.colorTextTertiary : token.colorPrimary,
                                        }}
                                    />
                                }
                                label={template.name}
                                selected={template.id === selectedTemplateId}
                                onSelect={() => onTemplateChange(template.id)}
                            />
                        </Col>
                    ))}
                </Row>
            </CompactCollapsible>
            <div style={{ marginBottom: '0.75rem', marginTop: '0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Customize Pipeline Stages</small>
            </div>
            {stagesEditor}
        </>
    );
};

export default memo(NewPipeline);
